import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Skeleton,
  Box,
} from '@mui/material';
import { ShoppingCart } from 'lucide-react';
import { getProducts } from '../firebase/products';
import { Product } from '../types';
import { useCartStore } from '../store/cartStore';
import toast from 'react-hot-toast';

interface ProductGridProps {
  categoryId?: string | null;
  mainCategoryId: string;
}

const stripHtml = (html: string) => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

export const ProductGrid: React.FC<ProductGridProps> = ({ categoryId, mainCategoryId }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const addToCart = useCartStore((state) => state.addItem);

  useEffect(() => {
    loadData();
  }, [categoryId, mainCategoryId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const productsData = await getProducts();
      setProducts(productsData);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Error al cargar los productos. Por favor, intenta de nuevo más tarde.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product: Product) => {
    addToCart(product);
    toast.success('Producto añadido al carrito');
  };

  const filteredProducts = products.filter(product => 
    product.categoryId === categoryId || product.subcategoryId === categoryId
  );

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(8)].map((_, i) => (
          <Card key={i} sx={{
            bgcolor: 'black',
            border: '1px solid rgba(0, 255, 0, 0.2)',
            boxShadow: '0 0 10px rgba(0, 255, 0, 0.1)',
          }}>
            <Skeleton 
              variant="rectangular" 
              height={300}
              sx={{ 
                bgcolor: 'rgba(0, 255, 0, 0.1)',
                transform: 'none'
              }} 
            />
            <CardContent>
              <Skeleton 
                height={24} 
                width="80%" 
                sx={{ 
                  bgcolor: 'rgba(0, 255, 0, 0.1)',
                  transform: 'none'
                }} 
              />
              <Skeleton 
                height={20} 
                width="60%" 
                sx={{ 
                  bgcolor: 'rgba(0, 255, 0, 0.1)',
                  mt: 1,
                  transform: 'none'
                }} 
              />
              <Skeleton 
                height={32} 
                width="40%" 
                sx={{ 
                  bgcolor: 'rgba(0, 255, 0, 0.1)',
                  mt: 2,
                  transform: 'none'
                }} 
              />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Box sx={{
        p: 4,
        bgcolor: 'rgba(255, 0, 0, 0.1)',
        border: '1px solid #ff0000',
        borderRadius: 2,
        color: '#ff0000',
      }}>
        {error}
      </Box>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {filteredProducts.map((product) => {
          const primaryImage = product.images.find(img => img.isPrimary);
          const description = stripHtml(product.shortDescription || product.description);
          
          return (
            <Card 
              key={product.id} 
              sx={{
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'black',
                border: '1px solid rgba(0, 255, 0, 0.2)',
                boxShadow: '0 0 10px rgba(0, 255, 0, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 0 20px rgba(0, 255, 0, 0.2)',
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <Link to={`/producto/${product.id}`}>
                <Box sx={{ 
                  position: 'relative',
                  paddingTop: '100%',
                  bgcolor: 'black',
                  overflow: 'hidden',
                }}>
                  {primaryImage && (
                    <CardMedia
                      component="img"
                      image={primaryImage.url}
                      alt={product.name}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '90%',
                        height: '90%',
                        objectFit: 'contain',
                        transition: 'transform 0.3s ease',
                        '&:hover': {
                          transform: 'translate(-50%, -50%) scale(1.05)',
                        },
                      }}
                    />
                  )}
                </Box>
              </Link>
              <CardContent sx={{ 
                flexGrow: 1, 
                display: 'flex', 
                flexDirection: 'column',
                bgcolor: 'rgba(0, 0, 0, 0.8)',
              }}>
                <Link
                  to={`/producto/${product.id}`}
                  style={{ textDecoration: 'none' }}
                >
                  <Typography
                    variant="h6"
                    component="h3"
                    className="font-orbitron"
                    sx={{
                      color: '#00ff00',
                      textShadow: '0 0 5px rgba(0, 255, 0, 0.3)',
                      mb: 1,
                      fontFamily: 'Orbitron, sans-serif',
                      fontWeight: 700,
                      fontSize: '1.1rem',
                      lineHeight: 1.2,
                      '&:hover': { color: '#00ff00cc' },
                    }}
                  >
                    {product.name}
                  </Typography>
                </Link>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#00ffff',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    mb: 2,
                    flexGrow: 1,
                    minHeight: '2.5rem',
                  }}
                >
                  {description}
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mt: 'auto',
                }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: '#00ff00',
                      textShadow: '0 0 5px rgba(0, 255, 0, 0.3)',
                    }}
                  >
                    {product.price}€
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<ShoppingCart className="w-4 h-4" />}
                    onClick={() => handleAddToCart(product)}
                    sx={{
                      bgcolor: '#00ff00',
                      color: 'black',
                      '&:hover': {
                        bgcolor: '#00cc00',
                      },
                    }}
                  >
                    Añadir
                  </Button>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredProducts.length === 0 && (
        <Box sx={{ 
          textAlign: 'center', 
          py: 12,
          color: '#00ffff',
        }}>
          <Typography variant="h6">
            No hay productos disponibles en esta categoría
          </Typography>
        </Box>
      )}
    </div>
  );
};

export default ProductGrid;